
import * as React from "react"

import { cn } from "@/lib/utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm",
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

/** The heading levels a card title may be rendered at. */
type CardTitleLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6"

/**
 * A card's heading.
 *
 * `as` EXISTS BECAUSE THE LEVEL IS A DOCUMENT DECISION, NOT A CARD DECISION.
 * This shipped hard-coded to `h3`, which is the right default for a card sitting
 * inside a section that already has an `h2` (most of them), but wrong wherever a
 * card is the first thing under the page's `h1`. That produced a real h1 -> h3
 * skip on /terms, /privacy-policy and /guidelines, where the "Key points" card
 * sits between the page title and the first clause heading, and again on /search
 * and /cart. Assistive technology reads the outline as a nested list, so a
 * skipped level reads as a missing section rather than a style choice, and it is
 * what axe reports as `heading-order` (WCAG 1.3.1).
 *
 * Defaulting to `h3` keeps every existing call site rendering exactly the markup
 * it rendered before, so this is additive: only the handful of places that were
 * actually skipping a level pass `as`.
 *
 * The ref type was also wrong (declared `HTMLParagraphElement` while rendering a
 * heading), and is corrected here rather than left to mislead the next caller.
 */
const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement> & { as?: CardTitleLevel }
>(({ className, as: Heading = "h3", ...props }, ref) => (
  <Heading
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
